/**
 * Layer 8 — battles and one-shot effects.
 *
 *  - Battle marker: crossed sabres on the contested edge with a balance bar (attacker colour vs
 *    defender colour) and small chevrons on every edge an attack comes across (flanking).
 *  - Particles (CPU pool → two instanced batches): muzzle flashes and night light pools
 *    (additive), drifting smoke (normal blend, tinted by daylight), artillery tracers on
 *    ballistic arcs ending in an impact flash. Emission scales with battle size and only runs
 *    for battles near the camera.
 *  - One-shot effects from `freshEvents`: city captured (flash + ink ripple in the new owner's
 *    colour), unit destroyed (smoke burst), decoy exposed / courier lost (red ripple).
 *  - Reduced motion: static markers only.
 */
import {
  type FactionId,
  type GameEvent,
  type GameMap,
  HEX_W,
  type HexGrid,
  NO_FACTION,
  type PlayerView,
} from '@friction/engine';
import { Container, type Texture } from 'pixi.js';
import type { MarkerGlyphs } from '../atlas/glyphs';
import type { Frame } from '../gfx/atlas';
import { QuadBatch, Scale } from '../gfx/quads';
import { darken, PAL } from '../palette';
import type { Rect } from '../sheet';

interface BattleFx {
  mx: number;
  my: number;
  dx: number;
  dy: number;
  attackers: [number, number][];
  intensity: number;
  balance: number;
  attColor: number;
  defColor: number;
  flashAcc: number;
  smokeAcc: number;
  shellAcc: number;
}

const K_FLASH = 0;
const K_SMOKE = 1;
const K_SHELL = 2;
const K_RIPPLE = 3;
const K_GLOW = 4;

const CAP = 4000;

export class BattlesLayer {
  readonly container = new Container({ label: 'battles' });
  /** Smoke (normal blend) — tinted by the renderer with the light colour. */
  readonly smokeMesh: QuadBatch['mesh'];
  private readonly markers: QuadBatch;
  private readonly smoke: QuadBatch;
  private readonly fire: QuadBatch;
  private readonly grid: HexGrid;
  private readonly map: GameMap;
  private readonly glyphs: MarkerGlyphs;
  private readonly white: Frame;
  private battles: BattleFx[] = [];
  // particle pool (structure of arrays)
  private n = 0;
  private readonly kind = new Uint8Array(CAP);
  private readonly px = new Float32Array(CAP);
  private readonly py = new Float32Array(CAP);
  private readonly vx = new Float32Array(CAP);
  private readonly vy = new Float32Array(CAP);
  private readonly age = new Float32Array(CAP);
  private readonly life = new Float32Array(CAP);
  private readonly s0 = new Float32Array(CAP);
  private readonly s1 = new Float32Array(CAP);
  private readonly a0 = new Float32Array(CAP);
  private readonly col = new Uint32Array(CAP);
  /** Shell arcs: target and apex height. */
  private readonly ex = new Float32Array(CAP);
  private readonly ey = new Float32Array(CAP);
  private readonly arc = new Float32Array(CAP);
  private lastEvents: GameEvent[] | null = null;

  constructor(map: GameMap, grid: HexGrid, glyphs: MarkerGlyphs, white: Frame, atlasTexture: Texture) {
    this.map = map;
    this.grid = grid;
    this.glyphs = glyphs;
    this.white = white;
    this.smoke = new QuadBatch('smoke', atlasTexture, 1024);
    this.fire = new QuadBatch('fire', atlasTexture, 1024);
    this.fire.mesh.blendMode = 'add';
    this.markers = new QuadBatch('battle-markers', atlasTexture, 256);
    this.smokeMesh = this.smoke.mesh;
    this.container.addChild(this.smoke.mesh, this.markers.mesh, this.fire.mesh);
  }

  rebuild(view: PlayerView, colors: ReadonlyMap<FactionId, number>): void {
    const g = this.grid;
    const colorOf = (h: number) => {
      const o = h >= 0 ? view.owner[h] : NO_FACTION;
      return o === NO_FACTION ? PAL.ink : (colors.get(o) ?? PAL.ink);
    };
    const old = new Map(this.battles.map((b) => [`${Math.round(b.dx)},${Math.round(b.dy)}`, b]));
    this.battles = view.battles.map((b) => {
      const dx = g.cx(b.hex);
      const dy = g.cy(b.hex);
      const attackers = b.attackerHexes
        .filter((h) => h >= 0)
        .map((h): [number, number] => [g.cx(h), g.cy(h)]);
      let vx = 0;
      let vy = 0;
      for (const [x, y] of attackers) {
        vx += x - dx;
        vy += y - dy;
      }
      const l = Math.hypot(vx, vy);
      const mx = l > 0 ? dx + (vx / l) * HEX_W * 0.45 : dx;
      const my = l > 0 ? dy + (vy / l) * HEX_W * 0.45 : dy;
      const prev = old.get(`${Math.round(dx)},${Math.round(dy)}`);
      return {
        mx,
        my,
        dx,
        dy,
        attackers,
        intensity: Math.sqrt(Math.max(1, b.attackers.length + b.defenders.length)),
        balance: b.balance,
        attColor: colorOf(b.attackerHexes[0] ?? -1),
        defColor: colorOf(b.hex),
        flashAcc: prev?.flashAcc ?? Math.random(),
        smokeAcc: prev?.smokeAcc ?? Math.random(),
        shellAcc: prev?.shellAcc ?? Math.random(),
      };
    });
  }

  /** One-shot effects for the events of a new tick (each events array handled once). */
  events(events: GameEvent[], colors: ReadonlyMap<FactionId, number>, reducedMotion: boolean): void {
    if (events === this.lastEvents) return;
    this.lastEvents = events;
    if (reducedMotion) return;
    const g = this.grid;
    for (const e of events) {
      if (e.kind === 'cityCaptured') {
        const city = this.map.cities[e.city];
        if (!city) continue;
        const x = g.cx(city.hex);
        const y = g.cy(city.hex);
        this.spawn(K_GLOW, x, y, 0, 0, 0.7, 60, 170, 0.9, PAL.flash);
        this.spawn(K_RIPPLE, x, y, 0, 0, 1.8, 16, 190, 0.95, darken(colors.get(e.to) ?? PAL.ink, 0.25));
        this.spawn(K_RIPPLE, x, y, 0, 0, 2.4, 10, 130, 0.6, darken(colors.get(e.to) ?? PAL.ink, 0.4));
        for (let i = 0; i < 6; i++) this.puff(x, y, 18);
      } else if (e.kind === 'unitDestroyed') {
        const x = g.cx(e.hex);
        const y = g.cy(e.hex);
        this.spawn(K_FLASH, x, y, 0, 0, 0.35, 20, 44, 1, PAL.flash);
        for (let i = 0; i < 10; i++) this.puff(x, y, 14, 1.6);
      } else if (e.kind === 'decoyExposed' || e.kind === 'courierLost') {
        const x = g.cx(e.hex);
        const y = g.cy(e.hex);
        this.spawn(K_RIPPLE, x, y, 0, 0, 1.5, 12, 110, 0.9, PAL.danger);
      } else if (e.kind === 'battleStarted') {
        this.spawn(K_FLASH, g.cx(e.hex), g.cy(e.hex), 0, 0, 0.25, 16, 36, 1, PAL.flash);
      }
    }
  }

  private spawn(
    k: number,
    x: number,
    y: number,
    vx: number,
    vy: number,
    life: number,
    s0: number,
    s1: number,
    a0: number,
    color: number,
  ): number {
    if (this.n >= CAP) return -1;
    const i = this.n++;
    this.kind[i] = k;
    this.px[i] = x;
    this.py[i] = y;
    this.vx[i] = vx;
    this.vy[i] = vy;
    this.age[i] = 0;
    this.life[i] = life;
    this.s0[i] = s0;
    this.s1[i] = s1;
    this.a0[i] = a0;
    this.col[i] = color;
    return i;
  }

  private puff(x: number, y: number, spread: number, lifeScale = 1): void {
    const r = Math.random() * spread;
    const a = Math.random() * Math.PI * 2;
    this.spawn(
      K_SMOKE,
      x + Math.cos(a) * r,
      y + Math.sin(a) * r,
      3 + Math.random() * 4,
      -2.5 - Math.random() * 3,
      (2.6 + Math.random() * 2) * lifeScale,
      8 + Math.random() * 5,
      26 + Math.random() * 16,
      0.5 + Math.random() * 0.2,
      Math.random() < 0.5 ? PAL.smoke : 0x857b6c,
    );
  }

  private emit(b: BattleFx, dt: number, night: number): void {
    const I = b.intensity;
    b.flashAcc += dt * 6 * I;
    b.smokeAcc += dt * 2 * I;
    b.shellAcc += dt * 0.3 * I;
    const r = HEX_W * 0.32;
    while (b.flashAcc >= 1) {
      b.flashAcc -= 1;
      const atDef = Math.random() < 0.55 || !b.attackers.length;
      const [cx, cy] = atDef ? [b.dx, b.dy] : b.attackers[(Math.random() * b.attackers.length) | 0];
      const a = Math.random() * Math.PI * 2;
      const d = Math.sqrt(Math.random()) * r;
      const x = cx + Math.cos(a) * d;
      const y = cy + Math.sin(a) * d;
      this.spawn(
        K_FLASH,
        x,
        y,
        0,
        0,
        0.08 + Math.random() * 0.1,
        7 + Math.random() * 4,
        12 + Math.random() * 6,
        1,
        PAL.flash,
      );
      if (Math.random() < 0.3 + 0.4 * night)
        this.spawn(K_GLOW, x, y, 0, 0, 0.25, 24, 56, 0.12 + 0.3 * night, 0xffc27a);
    }
    while (b.smokeAcc >= 1) {
      b.smokeAcc -= 1;
      const [cx, cy] =
        Math.random() < 0.6 || !b.attackers.length
          ? [b.dx, b.dy]
          : b.attackers[(Math.random() * b.attackers.length) | 0];
      this.puff(cx, cy, r);
    }
    while (b.shellAcc >= 1 && b.attackers.length) {
      b.shellAcc -= 1;
      const [ax, ay] = b.attackers[(Math.random() * b.attackers.length) | 0];
      // fired from a battery 1–2 hexes behind the attacking hex
      const bx = ax + (ax - b.dx) * (0.8 + Math.random() * 0.9) + (Math.random() - 0.5) * 30;
      const by = ay + (ay - b.dy) * (0.8 + Math.random() * 0.9) + (Math.random() - 0.5) * 30;
      const tx = b.dx + (Math.random() - 0.5) * r * 1.4;
      const ty = b.dy + (Math.random() - 0.5) * r * 1.4;
      const i = this.spawn(K_SHELL, bx, by, 0, 0, 0.9 + Math.random() * 0.4, 3.2, 3.2, 1, 0xfff0b0);
      if (i >= 0) {
        this.ex[i] = tx;
        this.ey[i] = ty;
        this.arc[i] = Math.hypot(tx - bx, ty - by) * (0.3 + Math.random() * 0.15);
      }
    }
  }

  /**
   * Per frame: markers, emission (battles inside `view` only), particle integration and
   * instance upload. `markerUnit` = world units per marker layout unit.
   */
  frame(
    dt: number,
    now: number,
    markerUnit: number,
    view: Rect,
    night: number,
    reducedMotion: boolean,
  ): void {
    const m = this.markers;
    m.setUnits(markerUnit, markerUnit);
    m.clear();
    const pulse = reducedMotion ? 1 : 1 + 0.07 * Math.sin(now / 180);
    const M = Scale.Marker;
    for (const b of this.battles) {
      for (const [ax, ay] of b.attackers) {
        const ex = (ax + b.dx) / 2;
        const ey = (ay + b.dy) / 2;
        const ch = this.glyphs.chevron;
        m.push(
          ex,
          ey,
          0,
          0,
          9,
          9,
          ch.frame,
          darken(b.attColor, 0.25),
          0.95,
          M,
          Math.atan2(b.dy - ay, b.dx - ax),
        );
      }
      const s = this.glyphs.sabres;
      m.push(b.mx, b.my, 0, 0, s.w * pulse, s.h * pulse, s.frame, 0xffffff, 1, M);
      const bw = 24;
      const share = Math.min(1, Math.max(0, (b.balance + 1) / 2));
      m.push(b.mx, b.my, 0, 15.5, bw + 2, 5.2, this.white, PAL.ink, 0.85, M);
      m.push(b.mx, b.my, 0, 15.5, bw, 3.4, this.white, b.defColor, 1, M);
      m.push(b.mx, b.my, -(bw * (1 - share)) / 2, 15.5, bw * share, 3.4, this.white, b.attColor, 1, M);
      if (
        !reducedMotion &&
        b.dx > view.x0 - 200 &&
        b.dx < view.x1 + 200 &&
        b.dy > view.y0 - 200 &&
        b.dy < view.y1 + 200
      ) {
        this.emit(b, dt, night);
      }
    }
    m.commit();
    this.integrate(dt);
  }

  private integrate(dt: number): void {
    const sm = this.smoke;
    const fi = this.fire;
    sm.clear();
    fi.clear();
    const gl = this.glyphs;
    const impacts: number[] = [];
    let w = 0;
    for (let i = 0; i < this.n; i++) {
      const age = this.age[i] + dt;
      if (age >= this.life[i]) continue;
      if (w !== i) this.copy(i, w); // compact in place
      this.age[w] = age;
      const t = age / this.life[w];
      const k = this.kind[w];
      if (k === K_SHELL) {
        const sx = this.px[w];
        const sy = this.py[w];
        const ex = this.ex[w];
        const ey = this.ey[w];
        // three trailing sparks along the ballistic arc
        for (let j = 0; j < 3; j++) {
          const tt = Math.max(0, t - j * 0.035);
          const x = sx + (ex - sx) * tt;
          const y = sy + (ey - sy) * tt - 4 * tt * (1 - tt) * this.arc[w];
          fi.push(
            x,
            y,
            0,
            0,
            3.4 - j * 0.8,
            3.4 - j * 0.8,
            gl.flash.frame,
            this.col[w],
            0.9 - j * 0.28,
            Scale.World,
          );
        }
        if (age + dt >= this.life[w]) impacts.push(ex, ey);
        w++;
        continue;
      }
      this.px[w] += this.vx[w] * dt;
      this.py[w] += this.vy[w] * dt;
      const size = this.s0[w] + (this.s1[w] - this.s0[w]) * (k === K_RIPPLE ? 1 - (1 - t) * (1 - t) : t);
      if (k === K_SMOKE) {
        const a = this.a0[w] * Math.min(1, t * 8) * (1 - t * t);
        sm.push(this.px[w], this.py[w], 0, 0, size, size, gl.softDot.frame, this.col[w], a, Scale.World);
      } else if (k === K_RIPPLE) {
        sm.push(
          this.px[w],
          this.py[w],
          0,
          0,
          size,
          size,
          gl.ring.frame,
          this.col[w],
          this.a0[w] * (1 - t),
          Scale.World,
        );
      } else {
        const a = this.a0[w] * (1 - t) * (k === K_FLASH ? 1 : 1 - t);
        fi.push(
          this.px[w],
          this.py[w],
          0,
          0,
          size,
          size,
          k === K_GLOW ? gl.glow.frame : gl.flash.frame,
          this.col[w],
          a,
          Scale.World,
        );
      }
      w++;
    }
    this.n = w;
    for (let j = 0; j < impacts.length; j += 2) {
      this.spawn(K_FLASH, impacts[j], impacts[j + 1], 0, 0, 0.16, 8, 18, 1, PAL.flash);
      this.puff(impacts[j], impacts[j + 1], 6, 1.2);
    }
    sm.commit();
    fi.commit();
  }

  private copy(from: number, to: number): void {
    this.kind[to] = this.kind[from];
    this.px[to] = this.px[from];
    this.py[to] = this.py[from];
    this.vx[to] = this.vx[from];
    this.vy[to] = this.vy[from];
    this.age[to] = this.age[from];
    this.life[to] = this.life[from];
    this.s0[to] = this.s0[from];
    this.s1[to] = this.s1[from];
    this.a0[to] = this.a0[from];
    this.col[to] = this.col[from];
    this.ex[to] = this.ex[from];
    this.ey[to] = this.ey[from];
    this.arc[to] = this.arc[from];
  }

  destroy(): void {
    this.markers.destroy();
    this.smoke.destroy();
    this.fire.destroy();
    this.container.destroy();
  }
}
